/**
 * Whether a jump from the configuration leads to a page dinoConnect can show.
 *
 * Messages, chips, cards and the menu name screens of the device. A screen the
 * device keeps from remote access (`remote: false`) is missing from the
 * configuration dinoConnect receives, and some screen types have no page here
 * yet; a jump to either would end on an empty page, so its trigger is not
 * offered. A jump to a page of the app itself (`chatbotPage`, `orderPage`) names
 * no screen of the configuration and always opens. `navigateTo` itself stays
 * open for deep links.
 */
import { getScreenComponent } from '@/components/screenComponents'
import { config } from '@/composables/useMqttConnection'
import { ScreenItemType, type IScreen, type ISidebarItem } from '@/interfaces/gui'
import { appPageOf } from '@/navigation/appPages'

/** The screen key of a reference: without a leading slash and without a `$card` suffix. */
export function screenKey(reference: string): string {
  const path = reference.startsWith('/') ? reference.slice(1) : reference
  return path.split('$')[0] ?? ''
}

/**
 * Whether the reference leads to a page dinoConnect can draw: an app page, or a
 * screen that exists and has a component; a viewPager needs one page it can draw.
 */
export function canShowScreen(
  reference: string | undefined,
  screens: Readonly<Record<string, IScreen>> | undefined,
): boolean {
  if (!reference) return false
  const key = screenKey(reference)
  if (appPageOf(key)) return true
  if (!screens) return false
  const screen = screens[key]
  if (!screen || !getScreenComponent(screen.type)) return false
  if (screen.type === 'viewPager') {
    return screen.pages.some((page) => {
      const child = screens[page]
      return !!child && !!getScreenComponent(child.type)
    })
  }
  return true
}

/** `canShowScreen` against the configuration in force. */
export function canOpen(reference: string | undefined): boolean {
  return canShowScreen(reference, config.value?.frontend?.screens)
}

function isSeparator(item: ISidebarItem): boolean {
  return item.type === ScreenItemType.Separator || item.type === ScreenItemType.Seperator
}

/**
 * The menu as far as it leads somewhere: jumps to pages dinoConnect cannot show
 * are left out, a group left without entries goes with them, and separators
 * stand only between entries, never twice in a row.
 */
export function reachableMenu(
  items: ReadonlyArray<ISidebarItem>,
  opens: (reference: string | undefined) => boolean = canOpen,
): ISidebarItem[] {
  const kept: ISidebarItem[] = []
  for (const item of items) {
    if (item.type === ScreenItemType.Screenjump) {
      if (opens(item.screen)) kept.push(item)
    } else if (item.type === ScreenItemType.Extend) {
      const children = reachableMenu(item.items ?? [], opens)
      if (children.length > 0) kept.push({ ...item, items: children })
    } else if (isSeparator(item)) {
      const last = kept[kept.length - 1]
      if (last && !isSeparator(last)) kept.push(item)
    } else {
      kept.push(item)
    }
  }
  while (kept.length > 0 && isSeparator(kept[kept.length - 1]!)) kept.pop()
  return kept
}
